import mitt from 'mitt';

import type { EventBusEvent } from '../enums/EventBus';

type EventPayload = object | string | boolean | number;

interface Emitter {
  on: <T>(name: string, handler: (event: T) => void) => void;
  off: <T>(name: string, handler: (event: T) => void) => void;
  emit: <T = EventPayload>(name: EventBusEvent, data: T) => void;
}

interface WithLatestEmitted {
  latestEmitted<T>(name: string): T | undefined;
}

interface EnhancedEmitter extends Emitter, WithLatestEmitted {}

type EventBusContext = {
  [k in symbol]: Emitter;
};

const createEmitter = (latestData = {}) => {
  const emitter = mitt() as ReturnType<typeof mitt> & WithLatestEmitted;

  emitter.on('*', (type, data) => {
    latestData[type] = data;
  });
  emitter.latestEmitted = (type: string) => latestData[type];

  return emitter;
};

/**
 * Returns global context or an empty object
 */
const getContext = () =>
  typeof window !== 'undefined' && window.window === window
    ? window
    : typeof global !== 'undefined' && global.global === global
      ? global
      : {};

/**
 * Creates an instance of event bus with
 * the shared, global emitter
 */
export class EventBus {
  // create namespace in global symbol registry
  static namespace = Symbol.for('EVENT_BUS_GLOBAL_NAMESPACE');
  emitter: EnhancedEmitter;
  context: EventBusContext;

  constructor() {
    this.context = getContext();

    if (!this.context[EventBus.namespace]) {
      this.context[EventBus.namespace] = createEmitter();
    }

    this.emitter = this.context[EventBus.namespace] as EnhancedEmitter;
  }

  /**
   * Subscribe handler to the event
   */
  on<T = any>(name: EventBusEvent, handler: (event: T) => void) {
    this.emitter.on(name, handler);
  }

  /**
   * Unsubscribe handler from the event
   */
  off<T = any>(name: EventBusEvent, handler: (event: T) => void) {
    this.emitter.off(name, handler);
  }

  /**
   * Publish event
   */
  emit<T>(name: EventBusEvent, data: T) {
    this.emitter.emit(name, data);
  }

  /**
   * Retrieve latest emitted value
   */
  latestEmitted<T>(name: EventBusEvent) {
    return this.emitter.latestEmitted<T>(name);
  }

  /**
   * Reset the event bus by creating a new emitter instance
   */
  reset() {
    this.context[EventBus.namespace] = createEmitter();
    this.emitter = this.context[EventBus.namespace] as EnhancedEmitter;
  }
}

const eventBusInstance = new EventBus();
export default eventBusInstance;
